/**
 * §P.11 · Material: el catálogo, el pedido de septiembre y lo que piden las familias.
 *
 * **Sin pasarela, sin comisión y sin envíos.** El pago sigue ocurriendo donde ocurre
 * hoy —en el club, en efectivo o por Bizum—. Lo que aporta el producto es la lista.
 */

export type CategoriaDeMaterial =
  'dobok' | 'cinturon' | 'proteccion' | 'merchandising' | 'otro';

export interface ArticuloDelCatalogo {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly category: CategoriaDeMaterial | null;
  /** **En céntimos enteros.** 45,00 € es `4500`. */
  readonly price_cents: number;
  readonly currency: string;
  /**
   * Las tallas exactas, tal cual se piden.
   *
   * `/summary` agrupa por esta cadena, así que **una talla mal escrita —«130 » con un
   * espacio— parte el pedido en dos líneas** y el proveedor recibe dos pedidos de uno
   * en vez de uno de dos. Por eso la pantalla las recorta antes de mandarlas.
   */
  readonly sizes: readonly string[];
  /** Lo que no se tiene en stock: separa «te lo doy ahora» de «entra en el pedido». */
  readonly on_demand: boolean;
  readonly asset_id: string | null;
  readonly active: boolean;
  readonly pending_requests: number;
  readonly created_at: string | Date;
  readonly updated_at: string | Date;
}

export type EstadoDeCampana = 'open' | 'closed' | 'ordered' | 'received';

export interface CampanaDeMaterial {
  readonly id: string;
  readonly name: string;
  readonly opens_on: string | Date;
  readonly closes_on: string | Date | null;
  readonly status: EstadoDeCampana;
  readonly request_count: number;
  readonly total_cents: number;
  readonly created_at: string | Date;
}

export type EstadoDeSolicitud =
  'requested' | 'ordered' | 'delivered' | 'cancelled';

export interface SolicitudDeMaterial {
  readonly id: string;
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly catalog_item_id: string;
  readonly item_name: string;
  readonly category: CategoriaDeMaterial | null;
  readonly campaign_id: string | null;
  readonly campaign_name: string | null;
  readonly size: string | null;
  readonly quantity: number;
  readonly comment: string | null;
  readonly status: EstadoDeSolicitud;
  readonly unit_price_cents: number;
  readonly total_cents: number;
  readonly requested_by: string | null;
  readonly requested_at: string | Date;
  readonly ordered_at: string | Date | null;
  readonly delivered_at: string | Date | null;
}

export interface LineaDelPedido {
  readonly catalog_item_id: string;
  readonly item_name: string;
  readonly category: CategoriaDeMaterial | null;
  readonly size: string | null;
  readonly quantity: number;
  readonly unit_price_cents: number;
  readonly total_cents: number;
  /** Quién pidió qué. Sin esto, la caja llega y nadie sabe de quién es cada dobok. */
  readonly students: readonly string[];
}

export interface ResumenDelPedido {
  readonly campaign_id: string;
  readonly name: string;
  readonly status: EstadoDeCampana;
  readonly closes_on: string | Date | null;
  readonly lines: readonly LineaDelPedido[];
  readonly total_items: number;
  readonly total_cents: number;
}

export interface AnuncioDeSegundaMano {
  readonly id: string;
  readonly seller_user_id: string;
  readonly seller_name: string;
  readonly title: string;
  readonly description: string | null;
  readonly category: string | null;
  readonly size: string | null;
  readonly price_cents: number | null;
  readonly status: 'active' | 'reserved' | 'sold' | 'withdrawn';
  readonly created_at: string | Date;
  /** Lo que dijeron las familias que lo denunciaron, sin quién. 17-10-2026. */
  readonly reports: readonly {
    readonly reason: string | null;
    readonly reported_at: string | Date;
  }[];
}

export const CATEGORIAS: readonly {
  id: CategoriaDeMaterial;
  etiqueta: string;
}[] = [
  { id: 'dobok', etiqueta: 'Dobok' },
  { id: 'cinturon', etiqueta: 'Cinturón' },
  { id: 'proteccion', etiqueta: 'Protecciones' },
  { id: 'merchandising', etiqueta: 'Del club' },
  { id: 'otro', etiqueta: 'Otro' },
];

const NOMBRES_DE_CATEGORIA: Readonly<Record<string, string>> =
  Object.fromEntries(CATEGORIAS.map((c) => [c.id, c.etiqueta]));

export const etiquetaDeCategoria = (c: string | null): string =>
  c === null ? 'Sin categoría' : (NOMBRES_DE_CATEGORIA[c] ?? c);

/**
 * Los cuatro estados de un pedido agrupado, dichos como los diría el maestro.
 *
 * `open` no es «abierto»: es «recogiendo», que dice lo que está pasando. Y cada uno
 * cambia lo que la familia ve, así que la palabra tiene que ser la misma en las dos
 * pantallas.
 */
export const ESTADOS_DE_CAMPANA: Readonly<Record<EstadoDeCampana, string>> = {
  open: 'Recogiendo pedidos',
  closed: 'Cerrada',
  ordered: 'Pedida al proveedor',
  received: 'Recibida',
};

export const ESTADOS_DE_SOLICITUD: Readonly<Record<EstadoDeSolicitud, string>> =
  {
    requested: 'Pedida por la familia',
    ordered: 'Cursada al proveedor',
    delivered: 'Entregada',
    cancelled: 'Anulada',
  };

/**
 * El recorrido va **en un sentido**: `requested → ordered → delivered`.
 *
 * Volver atrás responde `409 request_cannot_go_back`, y con razón: marcar como
 * «pedida» algo ya entregado no es corregir un error, es **perder la fecha de
 * entrega** — que es justo lo que el club enseña cuando una familia dice que no
 * recibió nada. La pantalla solo ofrece el paso siguiente.
 */
export function siguienteEstado(
  estado: EstadoDeSolicitud
): { id: EstadoDeSolicitud; etiqueta: string } | null {
  if (estado === 'requested')
    return { id: 'ordered', etiqueta: 'Cursar al proveedor' };
  if (estado === 'ordered')
    return { id: 'delivered', etiqueta: 'Marcar entregada' };
  return null;
}
